import React from 'react';
import { Receipt, Loader2, Pizza } from 'lucide-react';

export default function BillSummary({ items = [], menuItems = [], loading = false }) {

  // Quick lookup: menuItemId -> markedAsToppings (from the menu catalog)
  const toppingLookup = menuItems.reduce((acc, mi) => {
    acc[mi.id] = mi.markedAsToppings;
    return acc;
  }, {});

  // Group items by name for bill display
  const groupedItems = items.reduce((acc, item) => {
    const key = item.itemName || item.name;
    const isTopping = Boolean(
      toppingLookup[item.menuItemId] ||
      item.customization?.parentCartId ||
      item.markedAsToppings
    );

    if (!acc[key]) {
      acc[key] = {
        name: key,
        quantity: 0,
        unitPrice: item.unitPrice || item.price || 0,
        total: 0,
        isTopping,
      };
    }
    acc[key].quantity += item.quantity || 1;
    acc[key].total += (item.unitPrice || item.price || 0) * (item.quantity || 1);
    return acc;
  }, {});

  const billItems = Object.values(groupedItems);
  const grandTotal = billItems.reduce((sum, item) => sum + item.total, 0);
  const totalItems = billItems.reduce((sum, item) => sum + item.quantity, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (billItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <div className="bg-slate-100 rounded-full p-6 mb-4">
          <Receipt size={48} className="text-slate-400" />
        </div>
        <p className="text-slate-700 font-semibold mb-1">No items in bill</p>
        <p className="text-slate-500 text-sm">
          Send items to kitchen to see them here
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Bill Items */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {/* Table Header */}
          <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600">
            <div className="col-span-6">Item</div>
            <div className="col-span-2 text-center">Qty</div>
            <div className="col-span-2 text-right">Rate</div>
            <div className="col-span-2 text-right">Amount</div>
          </div>

          {/* Bill Items */}
          <div className="divide-y divide-slate-100">
            {billItems.map((item, idx) => (
              <div
                key={idx}
                className={`px-3 py-2 grid grid-cols-12 gap-2 text-sm hover:bg-slate-50 ${
                  item.isTopping ? 'bg-pink-50/40' : ''
                }`}
              >
                <div className="col-span-6 font-medium text-slate-900 truncate flex items-center gap-1.5">
                  {item.isTopping && (
                    <Pizza size={12} className="text-pink-600 flex-shrink-0" />
                  )}
                  <span className="truncate">{item.name}</span>
                </div>
                <div className="col-span-2 text-center text-slate-700">
                  {item.quantity}
                </div>
                <div className="col-span-2 text-right text-slate-600">
                  ₹{item.unitPrice.toFixed(0)}
                </div>
                <div className="col-span-2 text-right font-semibold text-slate-900">
                  ₹{item.total.toFixed(0)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bill Total */}
      <div className="border-t-2 border-slate-200 bg-white p-4 space-y-2">
        {/* Subtotal */}
        <div className="flex justify-between text-sm text-slate-600">
          <span>Subtotal ({totalItems} items)</span>
          <span className="font-semibold">₹{grandTotal.toFixed(0)}</span>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200"></div>

        {/* Grand Total */}
        <div className="flex justify-between items-center">
          <span className="font-bold text-lg text-slate-900">Total</span>
          <span className="text-2xl font-black text-teal-700">
            ₹{grandTotal.toFixed(0)}
          </span>
        </div>
      </div>
    </div>
  );
}